/**
 * FmCorridorTrajectoryChart.layout — the label collision machinery of the
 * corridor-trajectory figure: estimated text boxes, a keyed per-render
 * registry, series-stroke and gridline obstacles, and the deterministic
 * point-label plan. Axis-domain math lives in
 * FmCorridorTrajectoryChart.axes.
 *
 * No JSX and no Recharts here: everything is numbers in, numbers out, so the
 * figure's placement rules are testable and readable on their own.
 */
import type {
  FmStoryCorridorTrajectoryPath,
  FmStoryCorridorTrajectoryPoint,
} from './FmCorridorTrajectory.types';

/* ------------------------------------------------------------------------ *
 * Label collision layout
 *
 * Recharts invokes label renderers in child order with REAL pixel positions,
 * so a per-render registry of estimated text boxes gives an honest collision
 * test without measuring the DOM. Entries are keyed so a re-render replaces
 * (never duplicates) a label's box.
 *
 * Labels are tested against SERIES STROKES too (visual-QA F2): every drawn
 * series is registered as a data-space obstacle polyline up front, and the
 * registry learns the data→pixel transform from the coordinates Recharts
 * hands the label renderers. A label whose every candidate slot crosses a
 * stroke is rendered on an opaque band-colour plate instead of
 * transparently over the line.
 * ------------------------------------------------------------------------ */

/** Estimated glyph metrics for the fine label size — estimates, not claims. */
export const LABEL_CHAR_W = 6.5;
/** The subject label renders semibold (fm-fw 600): wider glyphs, wider box. */
export const SUBJECT_CHAR_W = 7.2;
export const LABEL_H = 12;
export const LABEL_ABOVE_DY = -9;
export const LABEL_BELOW_DY = 18;

/**
 * Muted rates take distinct colours by served order, identically on stroke and
 * legend swatch, so the zero-enforced boundary (three muted rates) stays
 * decodable. None reuses the enforced, floor or trajectory colours.
 */
const CORRIDOR_RATE_RAMP = [
  'var(--fm-corridor-rate-1)',
  'var(--fm-corridor-rate-2)',
  'var(--fm-corridor-rate-3)',
] as const;

export function corridorRateRampStroke(rateIndex: number): string {
  return CORRIDOR_RATE_RAMP[Math.min(rateIndex, 2)];
}

/** Clearance (px) a label box must keep from any rendered series stroke. */
export const SERIES_CLEARANCE = 6;

/** Candidate offsets above the subject dot, nearest first. */
export const SUBJECT_OFFSETS = [8, 20, 32] as const;
/** The subject ReferenceDot's rendered radius (r={6} in the chart). */
export const SUBJECT_DOT_R = 6;
/** Clearance around the dot so a tag never touches its stroke. */
export const SUBJECT_DOT_CLEARANCE = 4;

/**
 * Container width (px) below which trajectory labels collapse to the compact
 * plan — priority-ranked (operation labels outrank terminal years), text-
 * deduplicated across paths, each kept label on an opaque plate — the same
 * breakpoint at which the legend stacks its value column (the @container rule
 * in FmCorridorTrajectoryChart.css).
 */
export const COMPACT_LABEL_MAX_WIDTH = 520;

export interface LabelBoxRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function textBox(
  x: number,
  baselineY: number,
  text: string,
  anchor: 'middle' | 'end' | 'start' = 'middle',
  charW: number = LABEL_CHAR_W,
): LabelBoxRect {
  const width = text.length * charW;
  const x1 = anchor === 'middle' ? x - width / 2 : anchor === 'end' ? x - width : x;
  return { x1, y1: baselineY - LABEL_H, x2: x1 + width, y2: baselineY + 2 };
}

export function intersects(a: LabelBoxRect, b: LabelBoxRect): boolean {
  return a.x1 < b.x2 && b.x1 < a.x2 && a.y1 < b.y2 && b.y1 < a.y2;
}

/**
 * Segment-vs-rect intersection (Liang–Barsky clip): true when the segment
 * p1→p2 passes through the rect. Degenerate (point) segments test as
 * containment.
 */
export function segmentIntersectsRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rect: LabelBoxRect,
): boolean {
  const dx = x2 - x1;
  const dy = y2 - y1;
  let t0 = 0;
  let t1 = 1;
  const clips: ReadonlyArray<[number, number]> = [
    [-dx, x1 - rect.x1],
    [dx, rect.x2 - x1],
    [-dy, y1 - rect.y1],
    [dy, rect.y2 - y1],
  ];
  for (const [p, q] of clips) {
    if (p === 0) {
      if (q < 0) return false; // parallel and fully outside
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
  }
  return t0 <= t1;
}

interface DataPoint {
  x: number;
  y: number;
}

export interface CorridorLabelRegistry {
  place(key: string, box: LabelBoxRect): void;
  drop(key: string): void;
  collides(box: LabelBoxRect, ignoreKey: string): boolean;
  setSubjectAnchor(pixelX: number, pixelY: number): void;
  subjectBox(): LabelBoxRect | null;
  /**
   * Teach the registry one data→pixel correspondence. Recharts hands label
   * renderers real pixel positions for known data points; two observations
   * with distinct data x and y pin the affine axis transforms.
   */
  observe(dataX: number, dataY: number, pixelX: number, pixelY: number): void;
  /** Register a drawn series (data space) as a stroke obstacle. */
  addSeriesObstacle(id: string, points: readonly DataPoint[]): void;
  /**
   * Register the plot GRIDLINES (the explicit data-space tick values) as
   * stroke obstacles: CartesianGrid draws one hairline per tick, and a label
   * printed transparently across one is as unreadable as one across a series
   * stroke (visual-QA round-2 B1). Resolved through the learned transforms,
   * exactly like series obstacles — unresolvable ticks refuse to guess.
   */
  addGridObstacles(xTicks: readonly number[], yTicks: readonly number[]): void;
  /**
   * True when the box (inflated by SERIES_CLEARANCE) crosses any registered
   * series stroke. Obstacles whose pixel geometry cannot be resolved yet
   * (transform not learned) do not collide — the test refuses to guess.
   */
  collidesWithSeries(box: LabelBoxRect): boolean;
}

export function createCorridorLabelRegistry(subjectLabel: string): CorridorLabelRegistry {
  const boxes = new Map<string, LabelBoxRect>();
  const seriesObstacles = new Map<string, readonly DataPoint[]>();
  let gridXTicks: readonly number[] = [];
  let gridYTicks: readonly number[] = [];
  let subjectAnchor: { x: number; y: number } | null = null;
  // Affine axis transforms, learned from observed (data, pixel) pairs.
  let xObservation: { data: number; pixel: number } | null = null;
  let yObservation: { data: number; pixel: number } | null = null;
  let xScale: { k: number; b: number } | null = null;
  let yScale: { k: number; b: number } | null = null;
  return {
    place(key, box) {
      boxes.set(key, box);
    },
    drop(key) {
      boxes.delete(key);
    },
    collides(box, ignoreKey) {
      for (const [key, placed] of boxes) {
        if (key !== ignoreKey && intersects(box, placed)) return true;
      }
      const subject = this.subjectBox();
      return subject !== null && ignoreKey !== 'subject' && intersects(box, subject);
    },
    setSubjectAnchor(pixelX, pixelY) {
      subjectAnchor = { x: pixelX, y: pixelY };
      // Register the subject MARKER itself, not only its label: collides()
      // once saw only the label's box while a neighbouring tag slid into the
      // dot. Radius + clearance keeps offsetting labels genuinely clear.
      const r = SUBJECT_DOT_R + SUBJECT_DOT_CLEARANCE;
      boxes.set('subject-dot', {
        x1: pixelX - r,
        y1: pixelY - r,
        x2: pixelX + r,
        y2: pixelY + r,
      });
    },
    subjectBox() {
      const placed = boxes.get('subject');
      if (placed) return placed;
      if (!subjectAnchor) return null;
      // The subject label's HOME position (smallest offset above the dot);
      // the real label may bump upward, never downward into this zone.
      return textBox(
        subjectAnchor.x,
        subjectAnchor.y - 6 - SUBJECT_OFFSETS[0],
        subjectLabel,
        'middle',
        SUBJECT_CHAR_W,
      );
    },
    observe(dataX, dataY, pixelX, pixelY) {
      if (xScale === null) {
        if (xObservation === null) {
          xObservation = { data: dataX, pixel: pixelX };
        } else if (xObservation.data !== dataX) {
          const k = (pixelX - xObservation.pixel) / (dataX - xObservation.data);
          xScale = { k, b: pixelX - k * dataX };
        }
      }
      if (yScale === null) {
        if (yObservation === null) {
          yObservation = { data: dataY, pixel: pixelY };
        } else if (yObservation.data !== dataY) {
          const k = (pixelY - yObservation.pixel) / (dataY - yObservation.data);
          yScale = { k, b: pixelY - k * dataY };
        }
      }
    },
    addSeriesObstacle(id, points) {
      seriesObstacles.set(id, points);
    },
    addGridObstacles(xTicks, yTicks) {
      gridXTicks = xTicks;
      gridYTicks = yTicks;
    },
    collidesWithSeries(box) {
      const inflated: LabelBoxRect = {
        x1: box.x1 - SERIES_CLEARANCE,
        y1: box.y1 - SERIES_CLEARANCE,
        x2: box.x2 + SERIES_CLEARANCE,
        y2: box.y2 + SERIES_CLEARANCE,
      };
      if (xScale === null || yScale === null) return false;
      // Gridlines: one horizontal hairline per y tick, one vertical per x
      // tick (visual-QA round-2 B1). Same clearance as series strokes.
      for (const tick of gridYTicks) {
        const pixelY = yScale.k * tick + yScale.b;
        if (pixelY >= inflated.y1 && pixelY <= inflated.y2) return true;
      }
      for (const tick of gridXTicks) {
        const pixelX = xScale.k * tick + xScale.b;
        if (pixelX >= inflated.x1 && pixelX <= inflated.x2) return true;
      }
      for (const points of seriesObstacles.values()) {
        for (let index = 0; index + 1 < points.length; index += 1) {
          const a = points[index];
          const b = points[index + 1];
          if (
            segmentIntersectsRect(
              xScale.k * a.x + xScale.b,
              yScale.k * a.y + yScale.b,
              xScale.k * b.x + xScale.b,
              yScale.k * b.y + yScale.b,
              inflated,
            )
          ) {
            return true;
          }
        }
      }
      return false;
    },
  };
}

export interface PointLabelPlan {
  text: string;
  side: 'above' | 'below';
  hasOperation: boolean;
  /** Compact-plan labels always render on an opaque plate (visual-QA F10). */
  plate: boolean;
}

function pointLabelKey(point: FmStoryCorridorTrajectoryPoint, text: string): string {
  return `${point.x}|${point.y}|${text}`;
}

/**
 * A point coincident with the subject cedes its marker to the subject label.
 * When that point carries an operation, the label
 * shifts to the NEXT point of the SAME served year — the post-operation state
 * at the bottom of the drop — provided the path persists one and it is not
 * itself the subject. Returns the shift target, or null when nothing shifts.
 */
function operationShiftTarget(
  path: FmStoryCorridorTrajectoryPath,
  index: number,
  coincides: (point: FmStoryCorridorTrajectoryPoint) => boolean,
): { index: number; label: string } | null {
  const point = path.points[index];
  const label = point.operationLabel;
  const next = path.points[index + 1];
  if (!label || next === undefined) return null;
  if (next.yearLabel !== point.yearLabel || coincides(next)) return null;
  return { index: index + 1, label };
}

/**
 * Plan which trajectory points get labels and on which side (data-space,
 * deterministic): endpoints and operation points are labelled; a label goes
 * below when the previous label was above or the point sits in the plot's
 * upper third; and the reference path drops (never double-prints) a label
 * whose point + text an earlier path already labelled.
 *
 * SUBJECT OWNERSHIP: a trajectory point-label and the
 * subject label must never bind to the same marker. A point coincident with
 * the subject emits NO label of its own — and when that point carries an
 * operation, its label shifts to the NEXT point of the same served year (the
 * post-operation state at the bottom of the drop), so the decision-year
 * information survives beside the drop instead of contradicting the subject
 * tag on one dot.
 *
 * LABEL PRIORITY (visual-QA round-2 B6): operation labels outrank terminal
 * year labels — they are planned in point order, so a path's decision years
 * claim their slots before its terminal year does. In COMPACT mode (below
 * COMPACT_LABEL_MAX_WIDTH) every kept label renders on an opaque plate, and
 * labels de-duplicate by their TEXT across paths — two paths ending on the
 * same served year print that year once, never twice a thumb-width apart.
 */
export function planPathLabels(
  path: FmStoryCorridorTrajectoryPath,
  subject: { x: number; y: number },
  yDomain: readonly [number, number],
  occupied: Set<string>,
  compact = false,
): Map<number, PointLabelPlan> {
  const plan = new Map<number, PointLabelPlan>();
  const lastIndex = path.points.length - 1;
  const upperThirdY = yDomain[0] + ((yDomain[1] - yDomain[0]) * 2) / 3;
  let previousSide: 'above' | 'below' | null = null;
  const coincides = (point: FmStoryCorridorTrajectoryPoint): boolean =>
    point.x === subject.x && point.y === subject.y;
  // Operation labels displaced off the subject marker: target index -> label.
  const shiftedOperations = new Map<number, string>();
  path.points.forEach((point, index) => {
    if (coincides(point)) {
      // The subject label owns this marker (round-2 B2); any operation label
      // here shifts per operationShiftTarget above.
      const shift = operationShiftTarget(path, index, coincides);
      if (shift !== null) shiftedOperations.set(shift.index, shift.label);
      return;
    }
    const isEndpoint = index === 0 || index === lastIndex;
    const shiftedOperation = shiftedOperations.get(index);
    const operationLabel = point.operationLabel ?? shiftedOperation;
    if (!isEndpoint && !operationLabel) return;
    const text = operationLabel ? `${point.yearLabel} · ${operationLabel}` : point.yearLabel;
    // Compact mode de-duplicates by TEXT across paths; full
    // mode only drops exact coincident duplicates.
    const key = compact ? text : pointLabelKey(point, text);
    if (occupied.has(key)) return; // duplicate: drop, never double-print
    occupied.add(key);
    const side: 'above' | 'below' =
      shiftedOperation !== undefined || previousSide === 'above' || point.y > upperThirdY
        ? 'below'
        : 'above';
    previousSide = side;
    plan.set(index, {
      text,
      side,
      hasOperation: operationLabel !== undefined,
      plate: compact,
    });
  });
  return plan;
}
